import { Box, Checkbox, Divider, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";

import globalStyles from "../../../../../assets/styles/global.module.scss";
import popupStyles from "../../../../../assets/styles/componentStyles/globalPopupStyles.module.scss";
import styles from "../../../../../assets/styles/componentStyles/organizationTeamsStyles/teams.module.scss";

import GlobalButton from "../../../../utility/globalButton/button";
import Required from "../../../../utility/required";
import { createOrganizationTeamPayload, organizationTeamsData, organizationUserDataModel } from "../../../../../models/axiosModel";
import { useContext, useEffect, useMemo, useState } from "react";
import { useOrganizationTeamsStore } from "../../../../../store/organizationTeamsStore";
import { useShallow } from "zustand/react/shallow";
import agent from "../../../../../services/api";
import { UserContext, UserContextPayload } from "../../../../../context/userContext";
import { formatOrganizationTeam } from "../constant.teams";
import { useGenericStore } from "../../../../../store/genericStore";
import { getInitials } from "../../../../../pages/ruleBasedInbox/utils";

interface props {
    selectedTeam?: organizationTeamsData;
    organizationUserData: organizationUserDataModel[];
    getOrganizationTeamsListApiCall: () => void;
}

function AddEditTeamPopup(props: props) {

    const { selectedTeam, organizationUserData, getOrganizationTeamsListApiCall } = props;

    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;

    const { setCreateNewTeamPopup, setEditTeamPopup, setSelectedTeam } = useOrganizationTeamsStore(
        useShallow((state) => ({
            setCreateNewTeamPopup: state.setCreateNewTeamPopup,
            setEditTeamPopup: state.setEditTeamPopup,
            setSelectedTeam: state.setSelectedTeam,
        }))
    );

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    // Constants
    const popupTitle = useMemo(() => {
        return selectedTeam ? "Edit Team" : "Create New Team";
    }, [selectedTeam]);

    const [teamName, setTeamName] = useState(selectedTeam?.organization_team_name ?? "");
    const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);

    const getMemberAssignmentKey = (userAssignmentId: number | string) => String(userAssignmentId);

    // Table
    const totalUsers = organizationUserData?.length ?? 0;
    const getUserFullName = (user: organizationUserDataModel) => `${user?.private_user_first_name ?? ""} ${user?.private_user_last_name ?? ""}`.trim();

    const allSelected = useMemo(() => {
        return totalUsers > 0 && selectedMemberIds?.length === totalUsers;
    }, [selectedMemberIds.length, totalUsers]);

    const someSelected = useMemo(() => {
        return selectedMemberIds?.length > 0 && selectedMemberIds?.length < totalUsers;
    }, [selectedMemberIds.length, totalUsers]);

    // Select Members in Table
    const handleSelectMember = (userAssignmentId: number | string) => {
        const memberKey = getMemberAssignmentKey(userAssignmentId);
        setSelectedMemberIds((prev) => prev.includes(memberKey) ? (prev.filter((id) => id !== memberKey)) : [...prev, memberKey]);
    };

    const handleSelectAll = () => {
        if (allSelected) {
            setSelectedMemberIds([]);
        } else {
            setSelectedMemberIds(organizationUserData?.map((user) => getMemberAssignmentKey(user?.user_assignment_id)) ?? []);
        }
    };

    // Add Team
    const handleAddTeam = async () => {
        const payload: createOrganizationTeamPayload = {
            organization_team_name: teamName,
            user_assignment_ids: selectedMemberIds?.map((id) => Number(id)),
        };

        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }));

        const res = await agent.createOrganizationTeam.put(payload);

        if (res.status) {
            handleClosePopup();
            showSuccess("Team Added Successfully!")
            getOrganizationTeamsListApiCall();
        }
        else {
            handleError(res);
        }

        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
    };

    // Update Team
    const handleUpdateTeam = async () => {
        const payload: createOrganizationTeamPayload = {
            organization_team_id: selectedTeam?.organization_team_id,
            organization_team_name: teamName,
            user_assignment_ids: selectedMemberIds?.map((userAssignmentId) => Number(userAssignmentId)),
        };

        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }));

        const res = await agent.createOrganizationTeam.put(payload);

        if (res.status) {
            setSelectedTeam(formatOrganizationTeam(res?.data));
            handleClosePopup();
            showSuccess("Team Updated Successfully!")
            getOrganizationTeamsListApiCall();
        }
        else {
            handleError(res);
        }

        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
    };

    // Close Popup
    const handleClosePopup = () => {
        if (!selectedTeam) {
            setCreateNewTeamPopup(false);
        } else {
            setEditTeamPopup(false);
        }
    };

    // Disable Create/Update Button
    const disableButton = (teamName?.trim()?.length === 0) || (selectedMemberIds?.length === 0);

    useEffect(() => {
        setTeamName(selectedTeam?.organization_team_name ?? "");
        
        const teamUserAssignmentIds = new Set(selectedTeam?.organization_team_users?.map((user) => String(user.user_assignment_id)) ?? []);
        
        setSelectedMemberIds(
            organizationUserData
                ?.filter((user) => teamUserAssignmentIds.has(getMemberAssignmentKey(user?.user_assignment_id)))
                ?.map((user) => getMemberAssignmentKey(user?.user_assignment_id)) ?? []
        );
    }, [selectedTeam, organizationUserData]);

    return (
        <Grid container className={`${globalStyles.flexColumn} ${globalStyles.justifyCenter}`}>

            {/* Popup Title */}
            <Grid item className={globalStyles.textCenter}>
                <Typography className={popupStyles.popupTitleTypography}>
                    {popupTitle}
                </Typography>
            </Grid>

            <Divider className={popupStyles.popupTitleDivider} />

            {/* Popup Content */}
            <Grid item className={globalStyles.fullwidth}>
                <Grid container className={popupStyles.popupBodyGridContainer} gap={2}>

                    {/* Team Name */}
                    <Grid item xs={12} mb={2}>
                        <Box className={styles.teamNameContainer}>
                            <Typography className={`${styles.teamNameLabel} ${styles.teamDetailsHeaderTitle}`} noWrap>
                                Team Name <Required />
                            </Typography>
                            <TextField
                                fullWidth
                                value={teamName}
                                onChange={(e) => setTeamName(e.target.value)}
                                placeholder="Enter Team Name"
                                className={styles.editTeamPopupTextField}
                            />
                        </Box>
                    </Grid>

                    {/* Team Members */}
                    <Grid item mb={2} className={globalStyles.fullwidth}>
                        <Grid container className={styles.editTeamMembersHeader} alignItems="center" justifyContent="space-between">
                            <Typography className={styles.teamDetailsHeaderTitle}>
                                Team Members <Required />
                            </Typography>
                            <Typography className={styles.editTeamMembersTotalCount}>
                                {selectedMemberIds?.length ?? 0} of {totalUsers} selected
                            </Typography>
                        </Grid>

                        <TableContainer className={styles.editTeamMembersTableContainer}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={`${globalStyles.primaryFontSize} ${styles.editTeamMembersCheckboxCell}`}>
                                            <Checkbox
                                                color="secondary"
                                                checked={allSelected}
                                                indeterminate={someSelected}
                                                onChange={handleSelectAll}
                                                disabled={totalUsers === 0}
                                                className={styles.editTeamMembersCheckbox}
                                                inputProps={{ "aria-label": "select all members" }}
                                            />
                                        </TableCell>
                                        <TableCell className={`${globalStyles.primaryFontSize} ${styles.editTeamMembersInitialsCell}`} />
                                        <TableCell className={`${globalStyles.primaryFontSize} ${globalStyles.secondaryFontWeight}`}>
                                            Name
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {organizationUserData?.map((user) => {
                                        const memberKey = getMemberAssignmentKey(user?.user_assignment_id);
                                        const isSelected = selectedMemberIds?.includes(memberKey);
                                        
                                        return (
                                            <TableRow
                                                hover
                                                key={user?.user_assignment_id}
                                                onClick={() => handleSelectMember(memberKey)}
                                                className={globalStyles.clickable}
                                            >
                                                <TableCell className={styles.editTeamMembersCheckboxCell}>
                                                    <Checkbox
                                                        color="secondary"
                                                        checked={isSelected}
                                                        onClick={(e) => { e.stopPropagation() }}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            handleSelectMember(memberKey);
                                                        }}
                                                        className={styles.editTeamMembersCheckbox}
                                                        inputProps={{ "aria-label": `select ${getUserFullName(user)}` }}
                                                    />
                                                </TableCell>

                                                <TableCell className={styles.editTeamMembersInitialsCell}>
                                                    <Box className={styles.editTeamMemberInitials}>
                                                        {getInitials(user?.private_user_first_name, user?.private_user_last_name)}
                                                    </Box>
                                                </TableCell>

                                                <TableCell className={`${globalStyles.primaryFontSize} ${globalStyles.secondaryFontWeight}`}>
                                                    {getUserFullName(user)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    
                                    {totalUsers === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} className={styles.editTeamMembersEmptyCell}>
                                                No users available
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>

                </Grid>
            </Grid>

            {/* Buttons */}
            <Grid item xs={12} className={globalStyles.fullwidth}>
                <Grid container className={popupStyles.popupButtonContainer}>
                    <Grid item>
                        <GlobalButton
                            buttonText="Cancel"
                            sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                            className='borderedButtonStyle'
                            onClick={handleClosePopup}
                        />
                    </Grid>
                    <Grid item>
                        <GlobalButton
                            buttonText={selectedTeam ? "Save Changes" : "Create Team"}
                            sx={{ width: "150px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                            className='primaryButtonStyle' 
                            disabled={disableButton}
                            onClick={() => { selectedTeam ? handleUpdateTeam() : handleAddTeam() }}
                        />
                    </Grid>
                </Grid>
            </Grid>
            
        </Grid>
    );
}

export default AddEditTeamPopup;